import { useCallback, useEffect, useRef, useState } from 'react';
import axiosInstance from '../services/axiosInstance';

/**
 * Ricerca associati con debounce per le modali admin (nuovo ticket, attribuzione
 * del richiedente di una email). Estratta da `SupportCenterV2` per riusare la
 * stessa logica — stesso endpoint, stesso debounce, stessa soglia — senza
 * imporre un markup: ogni modale rende i risultati con il proprio stile.
 *
 * Senza il permesso `associates.read` l'endpoint risponde 403: in quel caso
 * `searchFailed` diventa true e la UI ricade sull'inserimento manuale dell'id.
 */

export interface AssociateLite {
  _id: string;
  nominativo?: string;
  codice?: string;
  email?: string;
  citta?: string;
  provincia?: string;
}

export interface AssociateSearch {
  query: string;
  setQuery: (v: string) => void;
  results: AssociateLite[];
  clearResults: () => void;
  searchFailed: boolean;
  reset: () => void;
  /**
   * Selezione di un risultato: scrive nel campo il nominativo scelto e chiude
   * il menu SENZA far ripartire la ricerca (altrimenti il testo appena inserito
   * innescherebbe un nuovo giro e il dropdown si riaprirebbe da solo).
   */
  selectAndClose: (label: string) => void;
}

/**
 * Ricerca di default: endpoint admin (`associates.read`). L'area rappresentante
 * ne passa una sua (lista associati del proprio ambito territoriale).
 */
export type AssociateSearcher = (term: string) => Promise<AssociateLite[]>;

const adminSearch: AssociateSearcher = async (term) => {
  const res = await axiosInstance.get('/admin/associates', { params: { search: term, limit: 10 } });
  return res.data?.data || [];
};

/** `enabled` a false sospende la ricerca (es. tab "Esterno" selezionata). */
export const useAssociateSearch = (
  enabled: boolean = true,
  searcher: AssociateSearcher = adminSearch
): AssociateSearch => {
  const [query, setQuery] = useState<string>('');
  const [results, setResults] = useState<AssociateLite[]>([]);
  const [searchFailed, setSearchFailed] = useState<boolean>(false);
  // Salta UN giro di ricerca: lo alza `selectAndClose`, perche il testo scritto
  // dalla selezione non e una nuova ricerca dell'utente.
  const skipNextRef = useRef<boolean>(false);

  useEffect(() => {
    if (!enabled) return;
    if (skipNextRef.current) {
      skipNextRef.current = false;
      return;
    }
    const term = query.trim();
    if (term.length < 2) {
      setResults([]);
      return;
    }
    // `cancelled` copre sia il debounce annullato sia lo smontaggio: una risposta
    // stantia non deve riaprire il menu con risultati di una query precedente.
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const found = await searcher(term);
        if (cancelled) return;
        setResults(found);
        setSearchFailed(false);
      } catch {
        if (cancelled) return;
        // Senza `associates.read` la ricerca non e disponibile: resta l'id manuale.
        setResults([]);
        setSearchFailed(true);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query, enabled, searcher]);

  const clearResults = useCallback(() => setResults([]), []);

  const selectAndClose = useCallback((label: string) => {
    skipNextRef.current = true;
    setQuery(label);
    setResults([]);
  }, []);

  const reset = useCallback(() => {
    skipNextRef.current = false;
    setQuery('');
    setResults([]);
    setSearchFailed(false);
  }, []);

  return { query, setQuery, results, clearResults, searchFailed, reset, selectAndClose };
};

export default useAssociateSearch;
